// §V2 EXACT PORT — Workspace (docs/design-spikes/pomela-platform-v2/index.html
// lines 445-551): screen-head with search cmd + "New app", a stat-grid of REAL
// derivable stats, All/Live/Drafts fpills with REAL counts filtering the grid,
// and .pcard cards per REAL app (live AppPreview tiles, real publish badge,
// real updated time). The spike's data-statehost pattern stays — but the state
// is driven by MyApps' REAL data flow (localStorage recents fast-paint +
// hydrateFromCloud durable server list + /v1/analytics rollup + identity-wipe
// listener), not the prototype's .state-switch togglers. The spike's hardcoded
// grid-2 activity/installs panels become the REAL deriveActivity feed and the
// REAL per-app install figures MyApps already renders.
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { useAuth, useToast } from '@vibeshell/hooks';
import { Ic, tt } from './icons';
import { t } from '../lib/i18n';
import { goNav } from '../lib/nav-items';
import { AppPreview } from '../components/AppPreview';
import { getRecentApps, removeRecentApp, type RecentApp } from '../lib/recent-apps';
import { deleteServerApp } from '../lib/creator-apps-api';
import {
  hydrateFromCloud,
  loadAnalyticsMapStrict,
  mergeWorkspace,
  deriveActivity,
  type WorkspaceApp,
  type AppAnalyticsRow
} from '../lib/workspace-data';

// Filter vocabulary matches MyApps/the editor: an app is 'draft' when it has an
// unpublished draft OR only a preview URL — never a fabricated status.
type Filter = 'all' | 'live' | 'draft';
const isDraftish = (a: WorkspaceApp): boolean => a.hasDraft === true || a.status === 'preview';

/** MyApps' exact relative-time vocabulary (same i18n keys). */
function relativeTime(ts: number): string {
  const min = Math.floor((Date.now() - ts) / 60000);
  if (min < 1) return t('ad.justNow');
  if (min < 60) return t('ad.minAgo', { n: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('ad.hourAgo', { n: hr });
  const days = Math.floor(hr / 24);
  if (days < 30) return t('ad.dayAgo', { n: days });
  return new Date(ts).toLocaleDateString();
}

/** Honest short form of the REAL app description for the .ac-by sub line. */
function truncate(s: string, max = 40): string {
  const clean = s.trim().replace(/\s+/g, ' ');
  return clean.length <= max ? clean : `${clean.slice(0, max - 1).trimEnd()}…`;
}

export function WorkspaceV2(): JSX.Element {
  // ─── MyApps' exact data flow: local cache fast-paint + cloud hydrate ──────
  const [raw, setRaw] = useState<RecentApp[]>(() => getRecentApps());
  const [analytics, setAnalytics] = useState<Map<string, AppAnalyticsRow>>(() => new Map());
  // '—' until the real /v1/analytics rollup has resolved — never a fake zero.
  const [analyticsReady, setAnalyticsReady] = useState(false);
  // REAL search over the workspace grid (name match, like MyApps' inline query)
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  // §S680 — no artificial timer: the skeleton shows ONLY while the genuine
  // cloud hydrate is in flight AND the local cache has nothing to paint.
  const [hydrating, setHydrating] = useState(true);
  // Honest error surface: offline at hydrate time with an empty cache means we
  // cannot know the truth — show retry instead of a false "No apps yet".
  const [offline, setOffline] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // §WS-DEL — per-card delete is two-step: the trash button arms a confirm on
  // THAT card (confirmId), and the row disappears ONLY after the server returns
  // a 2xx (deletingId marks the in-flight card). Nothing is removed optimistically.
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const { session } = useAuth();
  const toast = useToast();
  const rootRef = useRef<HTMLElement>(null);

  // Identity wipe (account hand-off) → re-read the recents bucket immediately.
  useEffect(() => {
    const onReset = (): void => setRaw(getRecentApps());
    window.addEventListener('pomela:workspace-reset', onReset);
    return () => window.removeEventListener('pomela:workspace-reset', onReset);
  }, []);

  // MyApps' exact hydrate flow (durable server list + Supabase fallback, then
  // the real analytics rollup) — re-runnable via the error state's Retry.
  useEffect(() => {
    let alive = true;
    setHydrating(true);
    setOffline(typeof navigator !== 'undefined' && navigator.onLine === false);
    void (async () => {
      const restored = await hydrateFromCloud();
      if (alive && restored) setRaw(getRecentApps());
      if (alive) setHydrating(false);
      const m = await loadAnalyticsMapStrict();
      if (alive && m) {
        setAnalytics(m);
        setAnalyticsReady(true);
      }
      // m === null → rollup fetch failed: keep the honest '\u2014' (never a fake 0)
    })();
    return () => {
      alive = false;
    };
  }, [attempt]);

  // ─── Derived view — every number on screen comes from these rows ──────────
  const apps = useMemo(() => mergeWorkspace(raw, analytics), [raw, analytics]);
  const counts = useMemo(
    () => ({
      all: apps.length,
      live: apps.filter((a) => a.status === 'live').length,
      draft: apps.filter(isDraftish).length
    }),
    [apps]
  );
  const q = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      apps
        .filter((a) => (filter === 'all' ? true : filter === 'live' ? a.status === 'live' : isDraftish(a)))
        .filter((a) => (q === '' ? true : (a.manifest?.name ?? a.prompt ?? '').toLowerCase().includes(q))),
    [apps, filter, q]
  );
  const activity = useMemo(() => deriveActivity(apps), [apps]);
  const totalInstalls = useMemo(() => apps.reduce((n, a) => n + (a.analytics?.installs ?? 0), 0), [apps]);
  // Real rows only — apps with a genuine non-zero install count (MyApps' rule).
  const installRows = useMemo(() => apps.filter((a) => (a.analytics?.installs ?? 0) > 0).slice(0, 4), [apps]);

  const wsState: 'loading' | 'loaded' | 'empty' | 'error' =
    hydrating && raw.length === 0 ? 'loading' : apps.length > 0 ? 'loaded' : offline ? 'error' : 'empty';

  // ─── The spike's behaviors, real ──────────────────────────────────────────
  // #newAppBtn2 / data-focus-composer → the real "fresh composer" flow (same
  // event the CommandPalette fires), then Home.
  const newApp = (): void => {
    window.dispatchEvent(new CustomEvent('pomela:new-project'));
    goNav('');
  };
  const openPalette = (): void => {
    window.dispatchEvent(new CustomEvent('pomela:open-palette'));
  };
  // data-go="app" → the addressable App hub; Edit → the addressable editor.
  const openHub = (appId: string): void => goNav(`apps/${appId}`);
  const openEditor = (appId: string): void => goNav(`apps/${appId}/edit`);

  // §WS-DEL — arm/disarm the per-card confirm, then delete server-first. The
  // gateway is authoritative (ownership-checked); we drop the card + local
  // recents cache ONLY on a 2xx. A non-2xx keeps the card and surfaces the error.
  const armDelete = (appId: string): void => setConfirmId((cur) => (cur === appId ? null : appId));
  const cancelDelete = (): void => setConfirmId(null);
  const confirmDelete = async (appId: string): Promise<void> => {
    if (deletingId) return;
    setDeletingId(appId);
    const res = await deleteServerApp(appId);
    setDeletingId(null);
    if (res.ok) {
      setConfirmId(null);
      removeRecentApp(appId);
      setRaw((prev) => prev.filter((a) => a.appId !== appId));
      toast.show(tt('v2.workspace.deleted', 'App deleted'), { variant: 'success' });
    } else {
      // card stays put — only a 2xx removes it (idempotent 404 is still a no-remove
      // here so a stale click never silently hides an app the user may still own)
      toast.show(tt('v2.workspace.deleteErr', "Couldn't delete this app. Please try again."), { variant: 'error' });
    }
  };

  // Scroll-reveal — the spike's exact observer (root = #canvas, .12 / -6%).
  // Re-armed per state flip so newly-mounted .sr/.sr-stagger blocks reveal.
  useEffect(() => {
    const rootEl = rootRef.current;
    if (!rootEl) return;
    if (!('IntersectionObserver' in window)) {
      rootEl.querySelectorAll('.sr,.sr-stagger').forEach((el) => el.classList.add('in'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      },
      { root: document.getElementById('canvas'), threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    const raf = requestAnimationFrame(() => {
      rootEl.querySelectorAll('.sr:not(.in),.sr-stagger:not(.in)').forEach((el) => io.observe(el));
    });
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [wsState]);

  const pillCls = (f: Filter): string => `fpill${filter === f ? ' on' : ''}`;
  const loadingStats = wsState === 'loading';

  return (
    <section className="screen on" data-screen="workspace" ref={rootRef}>
      <div className="wrap stack">
        <div className="screen-head">
          <div className="sh-grow">
            <h2 className="title-page">{t('sb.workspace.eyebrow')}</h2>
            <p className="sub-h">{tt('v2.workspace.sub', "Everything you've made, in one place.")}</p>
          </div>
          {/* REAL name search over the workspace list (palette stays on \u2318K) */}
          <label className="cmd only-desk" style="max-width:240px;cursor:text">
            <Ic n="search" />
            <input
              className="cmd-ph"
              type="search"
              value={query}
              placeholder={tt('v2.workspace.searchPh', 'Search apps…')}
              aria-label={t('sb.workspace.search')}
              onInput={(e) => setQuery((e.currentTarget as HTMLInputElement).value)}
              style="background:none;border:0;outline:none;color:var(--fg);font-family:inherit;min-width:0;padding:0"
            />
          </label>
          <button className="btn btn-primary" id="newAppBtn2" type="button" onClick={newApp}>
            <Ic n="plus" /> {t('wsx.newApp')}
          </button>
        </div>

        {/* ONLY stats derivable from the real rows: totals from the merged list,
            installs from the real /v1/analytics rollup. The spike's "published
            today" / "+6 this week" deltas have no real source — dropped. */}
        <div className="stat-grid sr-stagger">
          <div className="stat">
            <span className="s-ico"><Ic n="grid" /></span>
            <div className="s-num">{loadingStats ? '—' : counts.all}</div>
            <div className="s-lbl">{tt('v2.workspace.statApps', 'Apps in this workspace')}</div>
          </div>
          <div className="stat">
            <span className="s-ico"><Ic n="rocket" /></span>
            <div className="s-num">{loadingStats ? '—' : counts.live}</div>
            <div className="s-lbl">{t('sb.workspace.fLive')}</div>
          </div>
          <div className="stat">
            <span className="s-ico"><Ic n="pencil" /></span>
            <div className="s-num">{loadingStats ? '—' : counts.draft}</div>
            <div className="s-lbl">{tt('v2.workspace.statDrafts', 'Drafts')}</div>
          </div>
          <div className="stat">
            <span className="s-ico"><Ic n="download" /></span>
            <div className="s-num">{analyticsReady ? totalInstalls.toLocaleString() : '—'}</div>
            <div className="s-lbl">{t('sb.workspace.installs')}</div>
          </div>
        </div>

        <div className="row row-split">
          <div className="fpills" data-filter-scope="workspace">
            <button
              className={pillCls('all')}
              data-filter-value="all"
              type="button"
              aria-pressed={filter === 'all'}
              onClick={() => setFilter('all')}
            >
              {t('sb.workspace.fAll')} {!loadingStats && <span className="cnt">{counts.all}</span>}
            </button>
            <button
              className={pillCls('live')}
              data-filter-value="live"
              type="button"
              aria-pressed={filter === 'live'}
              onClick={() => setFilter('live')}
            >
              {t('sb.workspace.fLive')} {!loadingStats && <span className="cnt">{counts.live}</span>}
            </button>
            <button
              className={pillCls('draft')}
              data-filter-value="draft"
              type="button"
              aria-pressed={filter === 'draft'}
              onClick={() => setFilter('draft')}
            >
              {tt('v2.workspace.statDrafts', 'Drafts')} {!loadingStats && <span className="cnt">{counts.draft}</span>}
            </button>
          </div>
          {/* the spike's .state-switch is prototype-only — prod states are the real fetch */}
        </div>

        <div data-statehost="ws" data-state={wsState}>
          {/* LOADED */}
          {wsState === 'loaded' && (
            <div data-when="loaded" className="stack" style="gap:0">
              <div className="apps-grid sr-stagger" data-filter-target="workspace">
                {visible.map((a) => {
                  const name = a.manifest?.name || a.appId;
                  const desc = (a.manifest?.description || '').trim();
                  const timeStr = relativeTime(a.updatedAt ?? a.createdAt);
                  const live = a.status === 'live';
                  return (
                    <article
                      key={a.appId}
                      className="pcard"
                      style="cursor:pointer"
                      data-filter-cat={live ? 'live' : 'draft'}
                      onClick={() => openHub(a.appId)}
                    >
                      <div className="pcard-hero">
                        <span className={`pcard-badge badge${live ? ' ok' : ''} dot`}>
                          {live ? t('sb.workspace.fLive') : t('wsx.fDraft')}
                        </span>
                        {/* the spike's hand-built .mini phone → the REAL app, live */}
                        <AppPreview appId={a.appId} url={a.url} ogImageUrl={a.ogImageUrl} />
                      </div>
                      <div className="pcard-body">
                        <div className="ac-by">
                          <span className="ac-av">{(name.charAt(0) || 'P').toUpperCase()}</span>
                          {/* keyboard-accessible primary action (the article onClick is mouse-only
                              now that it is no longer role=button — avoids nested-interactive). */}
                          <button
                            type="button"
                            className="pcard-open"
                            style="flex:1;min-width:0;background:none;border:0;padding:0;margin:0;font:inherit;color:inherit;cursor:pointer;text-align:start;display:block"
                            aria-label={name}
                            onClick={(e) => { e.stopPropagation(); openHub(a.appId); }}
                          >
                            <b>{name}</b>
                            <span>{desc ? `${truncate(desc)} · ${timeStr}` : timeStr}</span>
                          </button>
                          <button
                            className="ic-act"
                            type="button"
                            style="margin-inline-start:auto"
                            aria-label={`${t('ad.edit')} · ${name}`}
                            title={t('ad.edit')}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditor(a.appId);
                            }}
                          >
                            <Ic n="pencil" />
                          </button>
                          <button
                            className="ic-act"
                            type="button"
                            aria-label={`${tt('v2.workspace.deleteApp', 'Delete app')} · ${name}`}
                            title={tt('v2.workspace.deleteApp', 'Delete app')}
                            aria-expanded={confirmId === a.appId}
                            onClick={(e) => {
                              e.stopPropagation();
                              armDelete(a.appId);
                            }}
                          >
                            <Ic n="trash" />
                          </button>
                        </div>
                        {/* §WS-DEL — armed confirm for THIS card; the row is
                            removed only after the server 2xx (confirmDelete). */}
                        {confirmId === a.appId ? (
                          <div
                            className="row gap8"
                            style="margin-top:10px;align-items:center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="sub-h" style="flex:1;min-width:0">
                              {tt('v2.workspace.deleteConfirm', 'Delete this app permanently?')}
                            </span>
                            <button
                              className="btn btn-ghost btn-sm"
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelDelete();
                              }}
                            >
                              {tt('v2.workspace.deleteCancel', 'Cancel')}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              type="button"
                              disabled={deletingId === a.appId}
                              onClick={(e) => {
                                e.stopPropagation();
                                void confirmDelete(a.appId);
                              }}
                            >
                              {deletingId === a.appId
                                ? tt('v2.workspace.deleting', 'Deleting…')
                                : tt('v2.workspace.deleteBtn', 'Delete')}
                            </button>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}

          {/* LOADING — the spike's skeleton pcards, verbatim */}
          {wsState === 'loading' && (
            <div data-when="loading" className="apps-grid">
              <article className="pcard">
                <div className="pcard-hero"><div className="sk" style="position:absolute;inset:14px;border-radius:16px" /></div>
                <div className="pcard-body"><div className="sk sk-line" style="width:52%" /><div className="sk sk-line" style="width:84%" /></div>
              </article>
              <article className="pcard">
                <div className="pcard-hero"><div className="sk" style="position:absolute;inset:14px;border-radius:16px" /></div>
                <div className="pcard-body"><div className="sk sk-line" style="width:60%" /><div className="sk sk-line" style="width:78%" /></div>
              </article>
              <article className="pcard">
                <div className="pcard-hero"><div className="sk" style="position:absolute;inset:14px;border-radius:16px" /></div>
                <div className="pcard-body"><div className="sk sk-line" style="width:48%" /><div className="sk sk-line" style="width:90%" /></div>
              </article>
            </div>
          )}

          {/* EMPTY — plus the honest signed-out affordance: the cloud list only
              hydrates for a signed-in creator, so offer the real sign-in route */}
          {wsState === 'empty' && (
            <div data-when="empty">
              <div className="panel empty">
                <span className="e-ic"><Ic n="grid" /></span>
                <h4>{t('sb.workspace.empty')}</h4>
                <p>{tt('v2.workspace.emptyBody', 'Describe your first app in a sentence and it will live here, ready to edit, publish and share.')}</p>
                <button className="btn btn-primary" type="button" onClick={newApp}>
                  <Ic n="plus" /> {t('sb.workspace.emptyBtn')}
                </button>
                {!session.signedIn && (
                  <button className="btn btn-tonal" type="button" onClick={() => goNav('account')}>
                    <Ic n="user" /> {t('set.signInSync')}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ERROR — offline with an empty cache: the truth is unknowable, so
              say so and offer a real retry (re-runs the exact hydrate flow) */}
          {wsState === 'error' && (
            <div data-when="error">
              <div className="panel empty">
                <span className="e-ic e-ic-danger"><Ic n="x" /></span>
                <h4>{t('state.offline')}</h4>
                <p>{tt('v2.workspace.offlineBody', "We can't reach your synced apps right now. Check your connection and try again.")}</p>
                <button className="btn btn-tonal" type="button" onClick={() => setAttempt((n) => n + 1)}>
                  <Ic n="history" /> {tt('v2.workspace.retry', 'Try again')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* the spike's hardcoded activity/installs panels → the REAL derived
            activity feed + the REAL per-app install figures (no invented rows) */}
        <div className="grid-2 sr">
          <div className="panel">
            <div className="panel-hd"><h3>{t('sb.workspace.activity')}</h3></div>
            <div className="list divide">
              {activity.length === 0 ? (
                <div className="lrow"><div className="l-txt"><span>{t('wsx.activityEmpty')}</span></div></div>
              ) : (
                activity.map((it) => {
                  const icon = it.kind === 'installs' ? 'download' : it.kind === 'updated' ? 'pencil' : 'plus';
                  const tone = it.kind === 'installs' ? ' ok' : ' brand';
                  const verb =
                    it.kind === 'installs' ? t('wsx.evInstalls') : it.kind === 'updated' ? t('wsx.evUpdated') : t('wsx.evCreated');
                  return (
                    <div className="lrow" key={`${it.appId}-${it.kind}-${it.ts}`}>
                      <span className={`l-ico${tone}`}><Ic n={icon} /></span>
                      <div className="l-txt">
                        <b>{verb} · {it.name}</b>
                        {it.detail && <span>{it.detail}</span>}
                      </div>
                      <span className="l-trail">{relativeTime(it.ts)}</span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          <div className="panel">
            <div className="panel-hd"><h3>{t('sb.workspace.installs')}</h3></div>
            <div className="panel-p">
              <div className="row" style="justify-content:space-between;margin-bottom:12px">
                <span className="sub-h">{tt('v2.workspace.installsTotal', 'Total across your apps')}</span>
                <b className="tabular s-num" style="font-size:22px">{analyticsReady ? totalInstalls.toLocaleString() : '—'}</b>
              </div>
              <div className="list divide">
                {installRows.length === 0 ? (
                  <div className="lrow" style="padding:9px 0"><div className="l-txt"><span>{t('wsx.noInstalls')}</span></div></div>
                ) : (
                  installRows.map((a) => (
                    <div className="lrow" style="padding:9px 0" key={a.appId}>
                      <div className="l-txt"><b>{a.manifest?.name || a.appId}</b></div>
                      <span className="l-trail tabular">{(a.analytics?.installs ?? 0).toLocaleString()}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
